import api from './api';

export const summarizationService = {
  // Start a summarization job
  startSummarization: async (summarizationData) => {
    try {
      const response = await api.post('/api/summarize', {
        transcription_job_id: summarizationData.transcriptionJobId,
        chunk_seconds: summarizationData.chunkSeconds || 120,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get summarization job status
  getSummarizationStatus: async (jobId) => {
    try {
      const response = await api.get(`/api/summarized_status/${jobId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get summarization results
  getSummarizationResult: async (jobId) => {
    try {
      const response = await api.get(`/api/summarized_result/${jobId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Terminate a summarization job
  terminateSummarization: async (jobId) => {
    try {
      const response = await api.post(`/api/terminate_summarization/${jobId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get generated summary by company, quarter, and date
  getGeneratedSummary: async (searchData) => {
    try {
      const response = await api.get('/api/generated_summary', {
        params: {
          company_name: searchData.companyName,
          quarter: searchData.quarter,
          date: searchData.date,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Validate summarization data
  validateSummarizationData: (summarizationData) => {
    const errors = {};

    // Validate transcription job ID
    if (!summarizationData.transcriptionJobId?.trim()) {
      errors.transcriptionJobId = 'Transcription job ID is required';
    } else if (!/^job_\d{17,20}$/.test(summarizationData.transcriptionJobId.trim())) {
      errors.transcriptionJobId = 'Transcription job ID must be in format: job_YYYYMMDDHHMMSS...';
    }

    // Validate chunk seconds
    if (summarizationData.chunkSeconds !== undefined) {
      const chunkSeconds = Number(summarizationData.chunkSeconds);
      if (isNaN(chunkSeconds) || chunkSeconds <= 0) {
        errors.chunkSeconds = 'Chunk seconds must be greater than 0';
      } else if (chunkSeconds > 600) {
        errors.chunkSeconds = 'Chunk seconds cannot exceed 600 (10 minutes)';
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  },

  // Get status display text
  getStatusDisplay: (status) => {
    const statusMap = {
      'created': 'Created',
      'queued': 'Queued',
      'running': 'Running',
      'completed': 'Completed',
      'failed': 'Failed',
      'cancelled': 'Cancelled',
      'terminated': 'Terminated',
    };
    return statusMap[status] || status;
  },

  // Get status color for UI
  getStatusColor: (status) => {
    const colorMap = {
      'created': 'blue',
      'queued': 'yellow',
      'running': 'blue',
      'completed': 'green',
      'failed': 'red',
      'cancelled': 'gray',
      'terminated': 'red',
    };
    return colorMap[status] || 'gray';
  },

  // Dashboard functionality
  getDashboardSummarizations: async (params = {}) => {
    try {
      const response = await api.get('/api/dashboard/summarizations', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getDashboardStats: async () => {
    try {
      const response = await api.get('/api/dashboard/stats');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getActiveJobs: async () => {
    try {
      const response = await api.get('/api/dashboard/active');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get summarization from database for completed jobs
  getSummarizationFromDB: async (jobId) => {
    try {
      const response = await api.get(`/api/dashboard/summarization/${jobId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get call insights for a job
  getCallInsights: async (jobId, llmService = 'gemini') => {
    try {
      const response = await api.post(`/api/dashboard/call_insights/${jobId}?llm_service=${llmService}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
}; 